import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiUser, FiArrowLeft, FiShare2, FiTwitter, FiFacebook, FiLinkedin, FiCopy, FiClock } = FiIcons;

function BlogPost() {
  const { slug } = useParams();

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  // Sample blog posts data (in a real app, this would come from your CMS/API)
  const blogPosts = {
    "digital-memory-wall-events": {
      title: "Why Every Event Deserves Its Own Digital Memory Wall",
      excerpt: "Discover how a digital memory wall transforms every event into a timeless experience — connecting people, photos, and stories in one living album.",
      author: "Sarah Chen",
      date: "March 18, 2024",
      readTime: "4 min read",
      category: "Digital Innovation",
      image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1200&h=600&fit=crop",
      content: `
        <p>Every event has a heartbeat — moments that deserve to live beyond the final day.</p>
        <p>In 2024, we believe those moments shouldn't disappear into phone galleries or chat folders. They deserve a home — a digital memory wall where everyone who was part of your story can contribute and relive it, together.</p>
        <h2>The Living Album Revolution</h2>
        <p>Whether it's a wedding, a concert, or a college reunion, a digital memory wall lets guests upload their photos instantly, turning your event into a living album that grows in real time. It's modern, emotional, and deeply personal — the new way of saying "we were here, together."</p>
        <p>Unlike traditional photo sharing that scatters memories across different platforms, a digital memory wall creates a unified space where every perspective comes together. Your guests become co-creators of your story, each adding their unique viewpoint to create something richer than any single photographer could capture.</p>
        <h2>More Than Just Photos</h2>
        <p>A digital memory wall isn't just about collecting photos — it's about preserving the emotion, the connections, and the shared experience that made your event special. When someone uploads a candid laugh from your birthday party or a heartfelt moment from your graduation, they're not just sharing an image; they're contributing to a collective memory.</p>
        <p>These walls become time capsules that you can revisit years later, instantly transporting you back to the feelings, the energy, and the joy of that moment. They capture not just how your event looked, but how it felt.</p>
        <h2>Building Connection Through Sharing</h2>
        <p>In our increasingly digital world, we often experience events through screens — taking photos to share later rather than being present. A digital memory wall flips this dynamic. It encourages guests to capture authentic moments while staying engaged with the event itself.</p>
        <p>The magic happens when guests see their photos alongside others', creating a mosaic of perspectives that no single person could have captured alone. Suddenly, your shy cousin's beautiful candid shot sits next to your best friend's hilarious group photo, and your event becomes a collaborative masterpiece.</p>
        <h2>Your Story, Preserved Forever</h2>
        <p>With 2024, every event page becomes an archive of emotion — beautifully designed, easy to share, and accessible forever. Your memories don't vanish after the celebration ends. They evolve — just like you.</p>
        <p>Years from now, you'll be able to show your children the joy on your wedding day, not just through the professional photos, but through the eyes of everyone who loved you enough to be there. You'll see the spontaneous moments, the behind-the-scenes laughter, and the genuine connections that made your day uniquely yours.</p>
        <h2>The Future of Event Memories</h2>
        <p>Digital memory walls represent more than just a technological advancement — they're a return to community storytelling in a digital age. They remind us that our most precious moments are made more meaningful when shared with the people who matter most.</p>
        <p>Every event deserves its own digital memory wall because every gathering of people who care about each other creates something worth preserving. These aren't just photos; they're proof that we were here, that we mattered to each other, and that together, we created something beautiful.</p>
        <div style="background: linear-gradient(135deg,#f0fdfa 0%,#ecfeff 50%,#fef7f0 100%);padding: 2rem;border-radius: 1rem;margin: 2rem 0;text-align: center;">
          <h3 style="color: #0f766e;margin-bottom: 1rem;">Want your next event to have its own digital memory wall?</h3>
          <p style="color: #374151;margin-bottom: 1.5rem;">Visit <strong>2024.events</strong> and make your story live forever.</p>
          <a href="https://wa.me/919059468240" target="_blank" rel="noopener noreferrer" style="background: linear-gradient(135deg,#14b8a6,#06b6d4);color: white;padding: 0.75rem 2rem;border-radius: 9999px;text-decoration: none;font-weight: 600;display: inline-block;transition: all 0.3s ease;">Create Your Memory Wall</a>
        </div>
      `
    },
    "creative-ways-encourage-photo-sharing": {
      title: "10 Creative Ways to Encourage Photo Sharing at Your Event",
      excerpt: "Transform your event into a memory-making machine with these proven strategies that get every guest excited about capturing and sharing moments.",
      author: "Sarah Chen",
      date: "March 15, 2024",
      readTime: "5 min read",
      category: "Event Tips",
      image: "https://images.unsplash.com/photo-1511578314322-379afb476865?w=1200&h=600&fit=crop",
      content: `
        <p>Creating an atmosphere where guests naturally want to capture and share memories is an art form. After organizing hundreds of successful events, we've discovered that the key isn't just asking people to take photos – it's about creating irresistible moments that guests can't help but document.</p>
        <h2>1. Create Instagram-Worthy Moments</h2>
        <p>Design specific areas of your event with photo opportunities in mind. Think beyond the traditional photo booth – consider:</p>
        <ul>
          <li>Flower walls with your event colors</li>
          <li>Neon signs with memorable quotes</li>
          <li>Interactive art installations</li>
          <li>Themed backdrops that tell your story</li>
        </ul>
        <h2>2. Gamify the Experience</h2>
        <p>Turn photo sharing into a fun competition. Create photo scavenger hunts where guests need to capture specific moments, people, or objects. Offer prizes for the most creative shots, best group photos, or funniest candid moments.</p>
        <h2>3. Make Sharing Effortless</h2>
        <p>The easier it is to share, the more photos you'll receive. Provide:</p>
        <ul>
          <li>QR codes at every table linking to your gallery</li>
          <li>Clear, simple instructions</li>
          <li>Staff members who can help with uploads</li>
          <li>Strong Wi-Fi throughout the venue</li>
        </ul>
        <h2>4. Lead by Example</h2>
        <p>As the host, be the first to start sharing. Your enthusiasm is contagious – when guests see you actively documenting moments, they'll naturally follow suit.</p>
        <h2>5. Create Shareable Content</h2>
        <p>Give guests content worth sharing by incorporating:</p>
        <ul>
          <li>Surprise performances or entertainment</li>
          <li>Unique food presentations</li>
          <li>Interactive experiences</li>
          <li>Memorable speeches or announcements</li>
        </ul>
        <p>Remember, the goal isn't just to collect photos – it's to create an experience so memorable that guests want to relive and share it with others. When you focus on creating genuine moments of joy, the photos will follow naturally.</p>
        <div style="background: linear-gradient(135deg,#f0fdfa 0%,#ecfeff 50%,#fef7f0 100%);padding: 2rem;border-radius: 1rem;margin: 2rem 0;text-align: center;">
          <h3 style="color: #0f766e;margin-bottom: 1rem;">Ready to create your photo-sharing event?</h3>
          <p style="color: #374151;margin-bottom: 1.5rem;">Let us help you make memories that last forever.</p>
          <a href="https://wa.me/919059468240" target="_blank" rel="noopener noreferrer" style="background: linear-gradient(135deg,#14b8a6,#06b6d4);color: white;padding: 0.75rem 2rem;border-radius: 9999px;text-decoration: none;font-weight: 600;display: inline-block;transition: all 0.3s ease;">Start Planning</a>
        </div>
      `
    },
    "psychology-of-memory": {
      title: "The Psychology of Memory: Why We Share Photos",
      excerpt: "Discover the fascinating science behind why humans are driven to capture and share experiences, and how it strengthens our connections.",
      author: "Dr. Emily Rodriguez",
      date: "March 12, 2024",
      readTime: "8 min read",
      category: "Psychology",
      image: "https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=1200&h=600&fit=crop",
      content: `
        <p>Have you ever wondered why we feel compelled to photograph almost every significant moment in our lives? The answer lies deep in our psychology and the fundamental human need for connection, validation, and memory preservation.</p>
        <h2>The Neuroscience of Memory and Photography</h2>
        <p>When we take a photo, our brain creates multiple types of memories simultaneously. The visual cortex processes the image, while the hippocampus encodes the emotional context. This dual encoding makes photographed memories more vivid and longer-lasting than those we simply experience without documentation.</p>
        <h2>Social Validation and Belonging</h2>
        <p>Sharing photos serves a crucial social function. When we post images from events, we're not just showing what we did – we're communicating our social connections, our experiences, and our identity to our network. The likes, comments, and shares we receive provide social validation and reinforce our sense of belonging.</p>
        <h2>The Storytelling Imperative</h2>
        <p>Humans are natural storytellers, and photos serve as the visual chapters of our personal narratives. Each image we share adds to the story we're telling about ourselves and our lives. Events become more meaningful when we can place them within the larger context of our ongoing story.</p>
        <h2>Digital Scrapbooking</h2>
        <p>Modern photo sharing is essentially digital scrapbooking. We're curating collections of moments that define important periods in our lives. The act of selecting, editing, and sharing photos helps us process experiences and decide which memories are worth preserving.</p>
        <h2>The Fear of Missing Out (FOMO)</h2>
        <p>Paradoxically, our desire to capture moments can sometimes prevent us from fully experiencing them. The pressure to document can create anxiety about missing the "perfect shot" or not having enough content to share.</p>
        <h2>Building Collective Memory</h2>
        <p>When multiple people photograph the same event, they create a collective memory that's richer and more complete than any individual's experience. This shared documentation helps groups bond and creates a sense of shared history.</p>
        <p>Understanding these psychological drivers can help event organizers create experiences that naturally encourage documentation and sharing, leading to richer memories for everyone involved.</p>
        <div style="background: linear-gradient(135deg,#f0fdfa 0%,#ecfeff 50%,#fef7f0 100%);padding: 2rem;border-radius: 1rem;margin: 2rem 0;text-align: center;">
          <h3 style="color: #0f766e;margin-bottom: 1rem;">Create events worth remembering?</h3>
          <p style="color: #374151;margin-bottom: 1.5rem;">Let science guide your event planning with 2024.</p>
          <a href="https://wa.me/919059468240" target="_blank" rel="noopener noreferrer" style="background: linear-gradient(135deg,#14b8a6,#06b6d4);color: white;padding: 0.75rem 2rem;border-radius: 9999px;text-decoration: none;font-weight: 600;display: inline-block;transition: all 0.3s ease;">Learn More</a>
        </div>
      `
    }
  };

  const currentPost = blogPosts[slug] || blogPosts["digital-memory-wall-events"];
  const currentUrl = `${window.location.origin}${window.location.pathname}`;

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(currentPost.title)}&url=${encodeURIComponent(currentUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(currentUrl);
    // You could add a toast notification here
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-rose-50 pt-16">
      {/* Hero Section */}
      <motion.section 
        className="py-12 px-4"
        initial="initial"
        animate="animate"
        variants={staggerContainer}
      >
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <motion.div className="mb-8" variants={fadeInUp}>
            <Link to="/blog" className="inline-flex items-center space-x-2 text-teal-600 hover:text-teal-700 font-medium transition-colors">
              <SafeIcon icon={FiArrowLeft} />
              <span>Back to Blog</span>
            </Link>
          </motion.div>

          {/* Category */}
          <motion.div className="mb-4" variants={fadeInUp}>
            <span className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
              {currentPost.category}
            </span>
          </motion.div>

          {/* Title */}
          <motion.h1 
            className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight"
            variants={fadeInUp}
          >
            {currentPost.title}
          </motion.h1>

          {/* Meta Information */}
          <motion.div 
            className="flex flex-wrap items-center space-x-6 text-gray-600 mb-8"
            variants={fadeInUp}
          >
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiUser} className="text-gray-400" />
              <span>{currentPost.author}</span>
            </div>
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiCalendar} className="text-gray-400" />
              <span>{currentPost.date}</span>
            </div>
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiClock} className="text-gray-400" />
              <span>{currentPost.readTime}</span>
            </div>
          </motion.div>

          {/* Share Buttons */}
          <motion.div 
            className="flex items-center space-x-4 mb-12"
            variants={fadeInUp}
          >
            <span className="text-gray-600 font-medium">Share:</span>
            <div className="flex space-x-2">
              <a 
                href={shareLinks.twitter} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-blue-400 hover:bg-blue-500 text-white rounded-full flex items-center justify-center transition-colors"
              >
                <SafeIcon icon={FiTwitter} />
              </a>
              <a 
                href={shareLinks.facebook} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center transition-colors"
              >
                <SafeIcon icon={FiFacebook} />
              </a>
              <a 
                href={shareLinks.linkedin} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-blue-700 hover:bg-blue-800 text-white rounded-full flex items-center justify-center transition-colors"
              >
                <SafeIcon icon={FiLinkedin} />
              </a>
              <button 
                onClick={copyToClipboard}
                className="w-10 h-10 bg-gray-600 hover:bg-gray-700 text-white rounded-full flex items-center justify-center transition-colors"
              >
                <SafeIcon icon={FiCopy} />
              </button>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Featured Image */}
      <motion.section 
        className="px-4 mb-16"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={fadeInUp}
      >
        <div className="max-w-5xl mx-auto">
          <div className="rounded-3xl overflow-hidden shadow-2xl">
            <img 
              src={currentPost.image} 
              alt={currentPost.title} 
              className="w-full h-96 md:h-[500px] object-cover" 
            />
          </div>
        </div>
      </motion.section>

      {/* Article Content */}
      <motion.article 
        className="px-4 pb-20"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <div className="max-w-4xl mx-auto">
          <motion.div 
            className="bg-white rounded-3xl p-8 md:p-12 shadow-lg prose prose-lg max-w-none"
            variants={fadeInUp}
          >
            <div 
              className="text-gray-700 leading-relaxed" 
              dangerouslySetInnerHTML={{ __html: currentPost.content }}
              style={{ fontSize: '1.125rem', lineHeight: '1.75' }}
            />
          </motion.div>
        </div>
      </motion.article>

      {/* Bottom Share Section */}
      <motion.section 
        className="py-16 px-4 bg-white border-t border-gray-100"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <div className="max-w-4xl mx-auto text-center">
          <motion.h3 
            className="text-2xl font-bold text-gray-900 mb-6"
            variants={fadeInUp}
          >
            Enjoyed this article? Share it with others!
          </motion.h3>
          <motion.div 
            className="flex justify-center items-center space-x-4"
            variants={fadeInUp}
          >
            <div className="flex space-x-3">
              <a 
                href={shareLinks.twitter} 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-blue-400 hover:bg-blue-500 text-white px-6 py-3 rounded-full font-semibold transition-colors flex items-center space-x-2"
              >
                <SafeIcon icon={FiTwitter} />
                <span>Tweet</span>
              </a>
              <a 
                href={shareLinks.facebook} 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-semibold transition-colors flex items-center space-x-2"
              >
                <SafeIcon icon={FiFacebook} />
                <span>Share</span>
              </a>
              <a 
                href={shareLinks.linkedin} 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-3 rounded-full font-semibold transition-colors flex items-center space-x-2"
              >
                <SafeIcon icon={FiLinkedin} />
                <span>Share</span>
              </a>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Back to Blog CTA */}
      <motion.section 
        className="py-16 px-4 bg-gradient-to-r from-teal-500 via-cyan-500 to-blue-600"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <div className="max-w-4xl mx-auto text-center">
          <motion.h3 
            className="text-3xl font-bold text-white mb-6"
            variants={fadeInUp}
          >
            More Stories & Insights
          </motion.h3>
          <motion.p 
            className="text-xl text-white/90 mb-8"
            variants={fadeInUp}
          >
            Discover more tips, stories, and insights about creating memorable events
          </motion.p>
          <motion.div variants={fadeInUp}>
            <Link 
              to="/blog"
              className="bg-white text-gray-900 px-8 py-4 rounded-full text-lg font-semibold shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 transform inline-block"
            >
              View All Articles
            </Link>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
}

export default BlogPost;